import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { buildCityService, buildBreadcrumb } from '../../../../lib/structuredData';
import Header from '../../../../components/ui/Header';
import Footer from '../../../../components/Footer';
import { openCustomerPortal } from '../../../../utils/cn';

const MAPBOX_TOKEN = import.meta.env?.VITE_MAPBOX_ACCESS_TOKEN;

const AddressInputWithMapbox = ({ placeholder, buttonText, buttonId, microcopy, darkMicrocopy }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchSuggestions = async (val) => {
    if (!val || val?.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(val)}.json?access_token=${MAPBOX_TOKEN}&types=address&country=US&proximity=-84.5120,39.1031&autoComplete=true`
      );
      const data = await res?.json();
      setSuggestions(data?.features || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const val = e?.target?.value;
    setQuery(val);
    clearTimeout(debounceRef?.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = (place) => {
    setQuery(place?.place_name || '');
    setSuggestions([]);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setSuggestions([]);
    // Carry the typed address into the portal so the wizard opens pre-filled.
    openCustomerPortal(query);
  };

  return (
    <div className="relative w-full">
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            value={query}
            onChange={handleChange}
            placeholder={placeholder}
            className="w-full px-5 py-4 rounded-full text-gray-800 text-sm outline-none border border-gray-200 focus:border-blue-400 bg-white"
            style={{ fontFamily: "'Inter', sans-serif", boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}
            autoComplete="off"
          />
          {suggestions?.length > 0 && (
            <div
              className="absolute top-full left-0 right-0 mt-1 bg-white rounded-2xl overflow-hidden z-50"
              style={{ boxShadow: '0 8px 32px rgba(0,1,80,0.14)', border: '1px solid rgba(7,176,233,0.15)' }}
            >
              {suggestions?.map((s) => (
                <button
                  key={s?.id}
                  type="button"
                  onClick={() => handleSelect(s)}
                  className="w-full text-left px-5 py-3 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-150 border-b border-gray-50 last:border-0"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <span className="font-medium text-gray-900">{s?.text}</span>
                  <span className="text-gray-400 ml-1">{s?.place_name?.replace(s?.text, '')}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="submit"
          className="px-8 py-4 rounded-full text-white font-bold text-sm whitespace-nowrap transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:scale-105"
          style={{ backgroundColor: '#07B0E9', boxShadow: '0 4px 20px rgba(7,176,233,0.35)', fontFamily: "'Poppins', 'Inter', sans-serif" }}
        >
          {loading ? 'Loading...' : buttonText}
        </button>
      </form>
      {microcopy && (
        <p className="text-center text-xs mt-3" style={{ color: darkMicrocopy ? '#6B7280' : 'rgba(255,255,255,0.65)', fontFamily: "'Inter', sans-serif" }}>
          {microcopy}
        </p>
      )}
      {/* Hidden Demand IQ trigger */}
      <button id="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea" className="hidden" aria-hidden="true" />
    </div>
  );
};

const CincinnatiServiceAreaPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA', fontFamily: "'Poppins', 'Inter', sans-serif" }}>
      <Helmet>
        <title>Top Rated Cincinnati Roofing Companies | Exact Online Roof Prices</title>
        <meta
          name="description"
          content="OpenbooQ brings transparent, affordable roofing to Cincinnati, Ohio. Compare shingles, get an exact price in about 30 minutes, and schedule your roof replacement online."
        />
        <meta name="keywords" content="cincinnati roofing companies, roofing companies cincinnati, cincinnati ohio roofing, roof replacement cincinnati, instant roof estimate cincinnati, greater cincinnati roofing, northern kentucky roofing" />
        <script type="application/ld+json">
          {JSON.stringify(buildCityService({ name: 'Cincinnati', region: 'OH', url: 'https://openbooq.com/service-areas/ohio/cincinnati' }))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(buildBreadcrumb([
            { name: 'Home', item: 'https://openbooq.com' },
            { name: 'Service Areas', item: 'https://openbooq.com/service-areas' },
            { name: 'Cincinnati, OH', item: 'https://openbooq.com/service-areas/ohio/cincinnati' },
          ]))}
        </script>
      </Helmet>
      <Header />
      {/* ===== HERO SECTION ===== */}
      <section
        className="relative min-h-screen flex items-center"
        style={{ backgroundColor: '#000150', paddingTop: '80px' }}
      >
        {/* Background image overlay */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/assets/images/cincinnati-ohio-skyline-at-night-1773088153554.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        {/* Dark navy tint overlay */}
        <div
          className="absolute inset-0"
          style={{ background: 'rgba(0,1,80,0.55)' }}
        />
        {/* Subtle grid */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hero-grid-cin" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#07B0E9" strokeWidth="0.8" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-grid-cin)" />
          </svg>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Headline + Trust Badges */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
                style={{ backgroundColor: 'rgba(7,176,233,0.15)', color: '#07B0E9', border: '1px solid rgba(7,176,233,0.3)' }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                Cincinnati, Ohio
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight"
                style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.02em' }}
              >
                Roofing Reimagined
                <br />
                <span style={{ color: '#07B0E9' }}>for Cincinnati, Ohio.</span>
              </h1>

              <p className="text-gray-300 text-lg sm:text-xl mb-8 leading-relaxed max-w-xl">
                Same premium materials. Better warranty. Thousands less. Skip the pushy sales reps and get an accurate, AI-powered roof replacement price in minutes.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 mb-10">
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <svg className="w-5 h-5" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="text-white text-xs font-bold uppercase tracking-wider">Energy Star Certified</span>
                </div>
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <svg className="w-5 h-5" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-white text-xs font-bold uppercase tracking-wider">Impact Resistant</span>
                </div>
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <svg className="w-5 h-5" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-white text-xs font-bold uppercase tracking-wider">30-Minute Roof Price</span>
                </div>
              </div>
            </div>

            {/* Right: White Card with Address Input */}
            <div className="flex justify-center lg:justify-end">
              <div
                className="w-full max-w-md bg-white rounded-2xl p-8"
                style={{ boxShadow: '0 24px 64px rgba(0,1,80,0.25), 0 4px 16px rgba(7,176,233,0.12)', borderRadius: '20px' }}
              >
                <div className="text-center mb-6">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3"
                    style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                    Free Roof Price
                  </div>
                  <h2
                    className="text-xl font-black mb-1"
                    style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}
                  >
                    Get My Roof Price
                  </h2>
                  <p className="text-gray-500 text-sm">Enter your Cincinnati address below</p>
                </div>

                <div className="space-y-3">
                  <AddressInputWithMapbox
                    placeholder="Enter your Cincinnati home address..."
                    buttonText="Get My Roof Price"
                    buttonId="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea"
                    microcopy={null}
                  />
                </div>

                <p
                  className="text-center text-xs mt-4"
                  style={{ color: '#6B7280', fontFamily: "'Inter', sans-serif" }}
                >
                  Free roof price • No sales calls • Serving Greater Cincinnati &amp; Northern KY
                </p>

                {/* Trust row */}
                <div className="flex items-center justify-center gap-4 mt-5 pt-5 border-t border-gray-100">
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" style={{ color: '#22C55E' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs text-gray-500" style={{ fontFamily: "'Inter', sans-serif" }}>No credit card needed</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" style={{ color: '#22C55E' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs text-gray-500" style={{ fontFamily: "'Inter', sans-serif" }}>Results in ~30 minutes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ===== BETTER WAY SECTION ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
                style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
              >
                A Smarter Way
              </div>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-black mb-6 leading-tight"
                style={{ color: '#000150', fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.02em' }}
              >
                A Smarter Alternative to Traditional Cincinnati Roofing Companies
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Let's face it: getting a new roof usually means taking time off work, waiting for contractors to show up, and sitting through high-pressure sales pitches. We built OpenbooQ to fix that. As the most innovative among cincinnati roofing companies, we allow you to design, purchase, and schedule your roof entirely from the comfort of your home.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Using our proprietary SmartPick™ AI technology, we measure your roof with pinpoint accuracy via satellite. This eliminates the overhead costs of traditional roofing companies cincinnati relies on, allowing us to pass thousands of dollars in savings directly to you—without sacrificing quality.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => { openCustomerPortal(); }}
                  className="px-8 py-4 rounded-full text-white font-bold text-sm transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:scale-105"
                  style={{ backgroundColor: '#07B0E9', boxShadow: '0 4px 20px rgba(7,176,233,0.35)', fontFamily: "'Poppins', sans-serif" }}
                >
                  Get My Free Roof Price
                </button>
                <Link
                  to="/how-it-works"
                  className="px-8 py-4 rounded-full font-bold text-sm transition-all duration-300 hover:shadow-md flex items-center justify-center gap-2"
                  style={{ color: '#000150', border: '2px solid #000150', fontFamily: "'Poppins', sans-serif" }}
                >
                  See How It Works
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
            {/* Right: SmartPick visual */}
            <div className="relative flex justify-center">
              <div
                className="relative w-full max-w-lg rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 24px 64px rgba(0,1,80,0.15)' }}
              >
                <img
                  src="/assets/images/ChatGPT_Image_Jan_12_2026_at_02_30_23_PM-1768246319404.png"
                  alt="SmartPick AI technology scanning a Cincinnati home roof with satellite wireframe overlay on a laptop screen"
                  className="w-full h-auto object-cover"
                />
                {/* Floating badge */}
                <div
                  className="absolute bottom-6 left-6 right-6 bg-white rounded-xl p-4"
                  style={{ boxShadow: '0 8px 24px rgba(0,1,80,0.12)' }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#EBF8FE' }}
                    >
                      <svg className="w-5 h-5" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-black" style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}>SmartPick™ AI</p>
                      <p className="text-xs text-gray-500" style={{ fontFamily: "'Inter', sans-serif" }}>Satellite-accurate roof measurement</p>
                    </div>
                    <div
                      className="ml-auto px-3 py-1 rounded-full text-xs font-bold"
                      style={{ backgroundColor: '#DCFCE7', color: '#16A34A' }}
                    >
                      Active
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ===== PREMIUM MATERIALS SECTION ===== */}
      <section className="py-24" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
              style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
            >
              Premium Materials
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4"
              style={{ color: '#000150', fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.02em' }}
            >
              Premium Shingles, Installed by Local Experts
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Greater Cincinnati weather is no joke — from Ohio River valley humidity to summer storms and winter ice. We install industry-leading, impact-resistant shingles built to endure it all.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* TAMKO */}
            <div
              className="bg-white rounded-2xl p-8 flex flex-col"
              style={{ boxShadow: '0 4px 24px rgba(0,1,80,0.07)', borderRadius: '12px' }}
            >
              <img
                src="/assets/images/tamko-titan-xt-rustic-hickory-house_1_-1768340280949.jpg"
                alt="TAMKO Titan XT shingle installed on a Cincinnati area home"
                className="w-full h-48 object-cover rounded-xl mb-6"
              />
              <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full self-start mb-3" style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}>TAMKO</span>
              <h3 className="text-xl font-black mb-2" style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}>Titan XT</h3>
              <p className="text-gray-500 text-sm mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>TAMKO's best-selling architectural shingle. Exceptional dimensional look and long-lasting performance for Greater Cincinnati homes.</p>
              {/* Color swatches */}
              <div className="flex gap-2 mb-5">
                <div className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: '#3D3D3D', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} title="Rustic Black" />
                <div className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: '#8B7355', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} title="Natural Timber" />
                <div className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: '#9E9E9E', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} title="Thunderstorm Grey" />
                <div className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: '#6B5B45', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} title="Rustic Cedar" />
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#07B0E9' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Limited Lifetime Warranty
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#07B0E9' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Impact Resistant
                </li>
              </ul>
              <Link
                to="/shingles"
                className="w-full py-3 rounded-full text-white font-bold text-sm text-center transition-all duration-300 hover:opacity-90 hover:shadow-lg block"
                style={{ backgroundColor: '#07B0E9', fontFamily: "'Poppins', sans-serif" }}
              >
                Select Product
              </Link>
            </div>

            {/* GAF */}
            <div
              className="bg-white rounded-2xl p-8 flex flex-col relative"
              style={{ boxShadow: '0 8px 32px rgba(7,176,233,0.18)', borderRadius: '12px', border: '2px solid rgba(7,176,233,0.3)' }}
            >
              <div
                className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest"
                style={{ backgroundColor: '#07B0E9', color: 'white', fontFamily: "'Poppins', sans-serif" }}
              >
                Most Popular
              </div>
              <img
                src="/assets/images/gaf-timberline-hdz-charcoal-house_1_-1768332752421.jpg"
                alt="GAF Timberline HDZ shingle in charcoal installed on a Cincinnati suburban home"
                className="w-full h-48 object-cover rounded-xl mb-6"
              />
              <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full self-start mb-3" style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}>GAF</span>
              <h3 className="text-xl font-black mb-2" style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}>Timberline HDZ</h3>
              <p className="text-gray-500 text-sm mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>America's #1-selling shingle. LayerLock technology and StainGuard Plus protection — the upgrade Cincinnati homeowners love.</p>
              {/* Color swatches */}
              <div className="flex gap-2 mb-5">
                <div className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: '#2C2C2C', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} title="Charcoal" />
                <div className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: '#8B7355', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} title="Hickory" />
                <div className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: '#A0A0A0', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} title="Pewter Gray" />
                <div className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: '#7A6A5A', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} title="Weathered Wood" />
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#07B0E9' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Limited Lifetime Warranty
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#07B0E9' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Energy Star Certified
                </li>
              </ul>
              <Link
                to="/shingles"
                className="w-full py-3 rounded-full text-white font-bold text-sm text-center transition-all duration-300 hover:opacity-90 hover:shadow-lg block"
                style={{ backgroundColor: '#07B0E9', fontFamily: "'Poppins', sans-serif" }}
              >
                Select Product
              </Link>
            </div>

            {/* Owens Corning */}
            <div
              className="bg-white rounded-2xl p-8 flex flex-col"
              style={{ boxShadow: '0 4px 24px rgba(0,1,80,0.07)', borderRadius: '12px' }}
            >
              <img
                src="/assets/images/owens-corning-oakridge-shingles-onyx-black-house_1_-1768334487021.jpg"
                alt="Owens Corning Duration shingle installed on a Cincinnati area home"
                className="w-full h-48 object-cover rounded-xl mb-6"
              />
              <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full self-start mb-3" style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}>Owens Corning</span>
              <h3 className="text-xl font-black mb-2" style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}>Duration</h3>
              <p className="text-gray-500 text-sm mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Owens Corning's flagship shingle with patented SureNail Technology. Unmatched curb appeal for Cincinnati's finest neighborhoods.</p>
              {/* Color swatches */}
              <div className="flex gap-2 mb-5">
                <div className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: '#1A1A1A', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} title="Onyx Black" />
                <div className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: '#8B7355', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} title="Brownwood" />
                <div className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: '#8C8C8C', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} title="Estate Gray" />
                <div className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: '#C4A882', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} title="Desert Tan" />
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#07B0E9' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Limited Lifetime Warranty
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#07B0E9' }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Energy Star Certified
                </li>
              </ul>
              <Link
                to="/shingles"
                className="w-full py-3 rounded-full text-white font-bold text-sm text-center transition-all duration-300 hover:opacity-90 hover:shadow-lg block"
                style={{ backgroundColor: '#07B0E9', fontFamily: "'Poppins', sans-serif" }}
              >
                Select Product
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* ===== CINCINNATI WEATHER / TRUST SECTION ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-2xl p-10 sm:p-14 text-white text-center"
            style={{ background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)', boxShadow: '0 24px 64px rgba(3,52,148,0.3)' }}
          >
            {/* Line-art weather icon */}
            <div className="flex justify-center mb-6">
              <svg className="w-16 h-16 opacity-90" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 64 64">
                <circle cx="32" cy="28" r="10" />
                <path d="M32 4v4M32 52v4M4 28h4M56 28h4M11.03 11.03l2.83 2.83M50.14 50.14l2.83 2.83M11.03 44.97l2.83-2.83M50.14 17.86l2.83-2.83" strokeLinecap="round" />
                <path d="M20 44c0-6.627 5.373-12 12-12s12 5.373 12 12" strokeLinecap="round" />
                <path d="M16 48h32" strokeLinecap="round" />
                <path d="M20 52h24" strokeLinecap="round" />
                <path d="M24 56h16" strokeLinecap="round" />
              </svg>
            </div>
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            >
              Built for Cincinnati's Climate
            </div>
            <h2
              className="text-3xl sm:text-4xl font-black mb-6"
              style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.02em' }}
            >
              Your Roof vs. Cincinnati's Weather
            </h2>
            <p className="text-white/85 text-lg leading-relaxed max-w-3xl mx-auto mb-10">
              Every premium shingle we install is rated for Ohio River valley conditions. Our impact-resistant products are engineered to handle Cincinnati's full seasonal assault — so you can stop worrying about your roof and start enjoying your home.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-10">
              {[
                { icon: '🌩️', label: 'Summer Storms' },
                { icon: '🧊', label: 'Winter Ice' },
                { icon: '💧', label: 'River Humidity' },
                { icon: '💨', label: 'High Winds' },
              ]?.map((item) => (
                <div key={item?.label} className="flex flex-col items-center gap-2">
                  <span className="text-3xl">{item?.icon}</span>
                  <span className="text-sm font-bold text-white/90" style={{ fontFamily: "'Poppins', sans-serif" }}>{item?.label}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => { openCustomerPortal(); }}
              className="px-10 py-4 rounded-full font-black text-sm transition-all duration-300 hover:scale-105 hover:shadow-xl"
              style={{ backgroundColor: 'white', color: '#033494', fontFamily: "'Poppins', sans-serif", boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}
            >
              Get My Cincinnati Roof Price
            </button>
          </div>
        </div>
      </section>
      {/* ===== FINANCING SECTION ===== */}
      <section className="py-24" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
              style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
            >
              Flexible Payment Options
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4"
              style={{ color: '#000150', fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.02em' }}
            >
              Transparent Pricing. No Surprises.
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Traditional estimates often change once the work starts. With OpenbooQ, the price you see is the price you pay. You're always in control.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Upfront */}
            <div
              className="bg-white rounded-2xl p-8 text-center"
              style={{ boxShadow: '0 4px 24px rgba(0,1,80,0.07)', borderRadius: '12px' }}
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ backgroundColor: '#EBF8FE' }}
              >
                <svg className="w-7 h-7" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-black mb-2" style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}>Pay Upfront</h3>
              <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>Pay 100% upfront and lock in the lowest possible price. No interest, no fees — just the best deal available.</p>
            </div>

            {/* Escrow */}
            <div
              className="bg-white rounded-2xl p-8 text-center"
              style={{ boxShadow: '0 4px 24px rgba(0,1,80,0.07)', borderRadius: '12px' }}
            >
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ backgroundColor: '#EBF8FE' }}
              >
                <svg className="w-7 h-7" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-black mb-2" style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}>Escrow Protection</h3>
              <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>Funds are held securely and only released when you're 100% satisfied with your new roof. Total peace of mind.</p>
            </div>

            {/* Finance */}
            <div
              className="bg-white rounded-2xl p-8 text-center relative"
              style={{ boxShadow: '0 8px 32px rgba(7,176,233,0.18)', borderRadius: '12px', border: '2px solid rgba(7,176,233,0.3)' }}
            >
              <div
                className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest"
                style={{ backgroundColor: '#07B0E9', color: 'white', fontFamily: "'Poppins', sans-serif" }}
              >
                Popular Choice
              </div>
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ backgroundColor: '#EBF8FE' }}
              >
                <svg className="w-7 h-7" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-black mb-2" style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}>Monthly Financing</h3>
              <div className="text-3xl font-black mb-2" style={{ color: '#07B0E9', fontFamily: "'Poppins', sans-serif" }}>$491<span className="text-base font-normal text-gray-400">/mo</span></div>
              <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>Flexible monthly payments starting from $491/month. Apply in minutes with no impact to your credit score.</p>
            </div>
          </div>
        </div>
      </section>
      {/* ===== BOTTOM CTA SECTION ===== */}
      <section
        className="py-24"
        style={{ backgroundColor: '#000150' }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
            style={{ backgroundColor: 'rgba(7,176,233,0.15)', color: '#07B0E9', border: '1px solid rgba(7,176,233,0.3)' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            Ready to Get Started?
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4"
            style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.02em' }}
          >
            Get Your Cincinnati Roof Price in 30 Minutes
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            No sales calls. No waiting. Just your exact price, in about 30 minutes.
          </p>
          <AddressInputWithMapbox
            placeholder="Enter your Cincinnati home address..."
            buttonText="Get My Roof Price"
            buttonId="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea"
            microcopy="Free roof price • No sales calls • Serving Greater Cincinnati & Northern KY"
            darkMicrocopy={false}
          />
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CincinnatiServiceAreaPage;
